import { createStore } from "vuex";
import http from "../modules/http";
import tags from "@/modules/tags";
import { firstStepsPending } from "@/modules/firstStepsCheck";
import router from "../router";
import dayjs from "dayjs";
import { resolveDefaultLanguage } from "../modules/defaultLanguage.mjs";
import {
  LOGOUT_FOLLOWING,
  followsLogout,
  logoutOf,
  sharedState,
  sharesLogout,
} from "../modules/tabSync.mjs";
import { setMoneyLanguageSource } from "../modules/formatMoney";

const store = createStore({
  state: {
    logged: false,
    user: null,
    appVersion: new Date().toISOString(),
    // the tenant has not finished the first steps: nothing but the first steps page opens until it does
    firstSteps: false,
    // the tenant's own data (its business name, tax codes and address): the first steps start the company from it
    tenant: null,
    token: null,
    logoutReason: null,
    sessionId: null,
    languages: [],
    defaultLanguage: null,
    currencies: [],
    countries: [],
    warehouses: [],
    enabledWarehouses: [],
    webshops: [],
    newPage: true,
    topBar: {
      q: "",
      date: [new Date(), new Date()],
      warehouse: null,
    },
    tags: {},
    pusher: {},
    modules: undefined,
    crmCategories: undefined,
    menuInfo: null,
    webshopBaseUrl: undefined,
    listOption: [],
    operations: [],
    pricePartners: [],
    appData: {},
    pageOption: undefined,
    showRightMenu: false,
    showSearchMenu: false,
    showClosedWarehouses: false,
    defaultApps: [],
    apps: [],
    filters: {},
    filterPage: "",
    filterOptions: [],
    filterOptionStock: false,
    filterOptionsPrice: true,
    apiError: null,
    apiSuccess: null,
    apiLoading: [],
    lastModal: null,
  },
  getters: {
    // the language the names of the tenant's data are shown in first (not English by default)
    defaultLanguage: (state) => resolveDefaultLanguage(state),
  },
  mutations: {
    setMenuInfo(state, data) {
      state.menuInfo = data ?? null;
    },
    setModule(state, { name, enabled }) {
      state.modules = { ...state.modules, [name]: enabled };
    },
    login(state, data) {
      state.logged = true;
      state.logoutReason = null;
      state.token = data.token;
      state.user = data.user;
      state.languages = data.languages;
      state.defaultLanguage = data.default_language ?? null;
      state.currencies = data.currencies;
      state.countries = data.countries;
      state.warehouses = data.warehouses;
      state.enabledWarehouses = data.enabled_warehouses;
      state.webshops = data.webshops;
      state.tags = data.tags;
      store.pusher = data.pusher;
      state.crmCategories = data.crm_categories;
      state.menuInfo = data.menu ?? null;
      state.modules = data.modules;
      state.webshopBaseUrl = data.webshop_base_url;
      state.sessionId = data.session_id;
      state.firstSteps = Boolean(data.tenant?.first_steps);
      state.tenant = data.tenant ?? null;

      tags.storeInit();
      refreshFirstSteps();
    },
    // a warehouse the user has just created: it is in their lists at once, without a new login
    addWarehouse(state, warehouse) {
      if (!state.warehouses.some((e) => e.id === warehouse.id)) {
        state.warehouses.push(warehouse);
      }
      if (!state.enabledWarehouses.includes(warehouse.id)) {
        state.enabledWarehouses.push(warehouse.id);
      }
    },
    setFirstSteps(state, value) {
      state.firstSteps = Boolean(value);
    },
    setToken(state, token) {
      state.token = token;
      state.logged = true;
      state.logoutReason = null;
    },
    // the payload is the reason ("user": the person logged out) or {reason, token} of the token the server refused
    logout(state, payload) {
      state.logoutReason = logoutOf(payload).reason;
      state.token = null;
      state.user = null;
      state.sessionId = null;
      state.firstSteps = false;
      state.logged = false;
      localStorage.removeItem("language");
    },
    editUser(state, user) {
      state.user.email = user.email;
      state.user.first_name = user.first_name;
      state.user.last_name = user.last_name;
      if (user.language_code) {
        state.user.language_code = user.language_code;
      }
    },
    editAvatar(state, avatar) {
      state.user.avatar_url = avatar;
    },
    toggleRightMenu(state) {
      state.showRightMenu = !state.showRightMenu;
    },
    toggleSearchMenu(state) {
      state.showSearchMenu = !state.showSearchMenu;
    },
    closeSearchMenu(state) {
      state.showSearchMenu = false;
    },
    setShowClosedWarehouses(state, val) {
      state.showClosedWarehouses = val;
    },
    setApiError(state, data) {
      state.apiError = data;
    },
    setApiSuccess(state, data) {
      state.apiSuccess = data;
    },
    setApiLoading(state, data) {
      let i = state.apiLoading.findIndex((e) => e.key === data.key);
      if (i < 0) {
        state.apiLoading.push(data);
      } else if (!data.loading) {
        state.apiLoading.splice(i, 1);
      }
    },
    showModal(state, data) {
      state.lastModal = data;
    },
    updateFilters(state, obj) {
      const filter = obj.filter;
      if (state.filters[state.filterPage] === undefined) {
        state.filters[state.filterPage] = {};
      }
      if (obj.value === null || obj.value === undefined) {
        delete state.filters[state.filterPage][filter];
      } else {
        let valueToStore;
        if (Array.isArray(obj.value)) {
          valueToStore = JSON.parse(JSON.stringify(obj.value));
        } else {
          valueToStore = obj.value;
        }
        state.filters[state.filterPage][filter] = {
          value: valueToStore,
        };
      }
    },
    syncFilterOptions(state) {
      if (!state.filterOptions || !state.filterPage) return;

      const currentFilters = state.filters[state.filterPage] || {};

      for (const filter of state.filterOptions) {
        const filterState = currentFilters[filter.key];
        const value =
          filterState && filterState.value !== undefined
            ? filterState.value
            : undefined;

        if (value === undefined || value === null) {
          if (filter.type === "number" || filter.type === "double") {
            filter.value = [0, filter.max || 100];
          } else if (filter.type === "date") {
            filter.value = ["", ""];
          } else {
            filter.value = {};
          }
        } else {
          if (filter.type === "number" || filter.type === "double") {
            if (typeof value === "string" && value.includes(",")) {
              filter.value = value.split(",").map(Number);
            } else if (Array.isArray(value)) {
              filter.value = [...value];
            }
          } else if (filter.type === "date") {
            if (typeof value === "string" && value.includes(",")) {
              filter.value = value.split(",");
            } else if (Array.isArray(value)) {
              filter.value = [...value];
            }
          } else {
            // Ensure multi-select filters always have an object as value in filterOptions
            const valObj = {};
            if (Array.isArray(value)) {
              for (const id of value) {
                valObj[id] = true;
              }
            } else if (typeof value === "object" && value !== null) {
              Object.assign(valObj, value);
            }
            filter.value = valObj;
          }
        }
      }
    },
    clearFilters(state) {
      state.filters[state.filterPage] = {};
    },
    addCrmCategory(state, data) {
      state.crmCategories.push(data);
    },
    updateCrmCategory(state, data) {
      const index = state.crmCategories.findIndex((c) => c.id === data.id);
      if (index !== -1) {
        state.crmCategories[index] = data;
      }
    },
    setOperations(state, data) {
      state.operations = data;
    },
    setPricePartners(state, data) {
      state.pricePartners = data;
    },
    clearListOption(state) {
      state.listOption = [];
    },
    addListOption(state, data) {
      state.listOption.push(data);
    },
    setPageOption(state, data) {
      state.pageOption = data;
    },
    storeAppData(state, data) {
      if (state.appData[data.appId] === undefined) {
        state.appData[data.appId] = {};
      }
      state.appData[data.appId][data.key] = data.value;
    },
    setTopBar(state, data) {
      if (state.topBar[data.key] === data.value) return;

      if (Array.isArray(data.value) && Array.isArray(state.topBar[data.key])) {
        if (
          data.value.length === state.topBar[data.key].length &&
          data.value.every((v, i) => {
            const currentVal = state.topBar[data.key][i];
            if (v instanceof Date && currentVal instanceof Date) {
              return v.getTime() === currentVal.getTime();
            }
            return v === currentVal;
          })
        ) {
          return;
        }
      }

      state.topBar[data.key] = data.value;
      if (
        data.key !== "q" &&
        data.notify !== false &&
        typeof data.value !== "object"
      ) {
        bc.postMessage({
          type: "topBar",
          key: data.key,
          value: data.value,
        });
      }
    },
    setFilterPage(state, data) {
      if (state.filters[data] === undefined) {
        state.filters[data] = {};
      }
      if (state.filterPage !== data) {
        state.filterPage = data;
      }
    },
    setFilterOptions(state, data) {
      state.filterOptions = data;
    },
    setFilterOptionsStock(state, data) {
      state.filterOptionStock = data;
    },
    setFilterOptionsPrice(state, data) {
      state.filterOptionsPrice = data;
    },
    addApp(state, app) {
      state.apps.push(app);
    },
    addDefaultApp(state, app) {
      state.defaultApps.push(app);
    },
    // a tag made elsewhere (websocket): only into the lists already loaded — a list loaded later brings it
    // anyway — and not twice
    addTag(state, tag) {
      for (const type of ["products", "orders", "crmItem"]) {
        const list = state.tags[type];
        if (Array.isArray(list) && !list.some((t) => t?.id === tag?.id)) {
          list.push(tag);
        }
      }
    },
    setPusher(state, data) {
      state.pusher = data;
    },
    initialiseStore(state) {
      if (localStorage.getItem("store")) {
        const rows = JSON.parse(localStorage.getItem("store"));
        for (const rowsKey in rows) {
          state[rowsKey] = rows[rowsKey];
        }
      }
      if (
        Array.isArray(state.topBar.date) &&
        (state.topBar.date[0] instanceof Date ||
          typeof state.topBar.date[0] === "string")
      ) {
        state.topBar.date[0] = dayjs()
          .utcOffset(0)
          .subtract(365, "day")
          .startOf("day")
          .toDate();
        state.topBar.date[1] = dayjs().utcOffset(0).startOf("day").toDate();
      }
      if (state.token !== null) {
        http.fetch("/users/me").then((data) => {
          state.user = data.user;
          state.sessionId = data.session_id;
          state.languages = data.languages;
          state.defaultLanguage = data.default_language ?? null;
          state.currencies = data.currencies;
          state.countries = data.countries;
          state.warehouses = data.warehouses;
          state.enabledWarehouses = data.enabled_warehouses;
          state.webshops = data.webshops;
          state.tags = data.tags;
          state.pusher = data.pusher;
          state.crmCategories = data.crm_categories;
          state.menuInfo = data.menu ?? null;
          state.modules = data.modules;
          state.webshopBaseUrl = data.webshop_base_url;

          tags.storeInit();

          state.firstSteps = Boolean(data.tenant?.first_steps);
          state.tenant = data.tenant ?? null;
          if (state.firstSteps) {
            router.push("/first-steps");
          }
          refreshFirstSteps();
        });
      }
      setTimeout(function () {
        bc.postMessage({
          type: "newPageTopBar",
        });
      }, 200);
      setTimeout(function () {
        state.newPage = false;
      }, 600);
    },
  },
  actions: {},
  modules: {},
});

// the backend says the first steps are pending (first_steps): then the required data is checked, and the flag is
// dropped when it is all there already. A tenant the backend does not send there is not checked at all (no extra requests)
function refreshFirstSteps() {
  if (!store.state.firstSteps) {
    return;
  }
  firstStepsPending(store.state).then((pending) => {
    if (pending === null || pending === store.state.firstSteps) {
      return;
    }
    store.commit("setFirstSteps", pending);
    if (pending) {
      router.push("/first-steps");
    }
  });
}

function readShared() {
  try {
    return JSON.parse(localStorage.getItem("store"));
  } catch (e) {
    return null;
  }
}

// amounts are written in the language of the signed in user
setMoneyLanguageSource(() => store.state.user?.language_code);

store.subscribe((mutation, state) => {
  // a token the server refused logs out this tab only: another tab may hold a newer, good one (see tabSync)
  if (
    mutation.type === "logout" &&
    !sharesLogout(logoutOf(mutation.payload), readShared())
  ) {
    return;
  }
  localStorage.setItem("store", JSON.stringify(sharedState(state)));
  if (state.user?.language_code) {
    localStorage.setItem("language", state.user.language_code);
  }
});

// another tab logged in or out: follow it, so every tab uses the same token
window.addEventListener("storage", (event) => {
  if (event.key !== "store" || event.newValue === null) {
    return;
  }
  let shared;
  try {
    shared = JSON.parse(event.newValue);
  } catch (e) {
    return;
  }
  if (shared.token && shared.token !== store.state.token) {
    const token = shared.token;
    store.commit("setToken", token);
    http.fetch("/users/me").then((data) => {
      store.commit("login", { ...data, token });
      if (router.currentRoute.value.name === "Login") {
        router.push("/");
      }
    });
  } else if (followsLogout(shared, store.state.logged)) {
    store.commit("logout", LOGOUT_FOLLOWING);
    router.push("/login");
  }
});

const bc = new BroadcastChannel("store");

bc.onmessage = (event) => {
  console.log(event);
  if (event.data.type === "newPageTopBar" && !store.state.newPage) {
    bc.postMessage({
      type: "topBar",
      key: "warehouse",
      value: store.state.topBar.warehouse,
    });
  } else if (event.data.type === "topBar") {
    if (store.state.topBar[event.data.key] !== event.data.value) {
      store.commit("setTopBar", {
        key: event.data.key,
        value: event.data.value,
        notify: false,
      });
    }
  }
};

export default store;
